import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { jwtDecode } from 'jwt-decode'
import SegmentService from 'redux/config/services/SegmentService'
import {
  LoginPayload,
  SocialLoginPayload,
  SocialSignupPayload,
  SsoLoginPayload,
  User,
  UserApiType as UserSuccessPayload,
} from 'redux/user/userTypes'
import LocalStorage from 'shared/LocalStorage'
import { setFleetPlatformType } from 'shared/Router/Utils'
import { getTokenExpirationFromCookies } from 'shared/JwtCookieUtils'
import {
  clearStoredTokenExp,
  setStoredTokenExp,
} from 'shared/auth/tokenStorage'

export interface UserState {
  profile: User
  error: {
    status: boolean
    info: {
      code: number
      statusText: string
      message: string
    } | null
  } | null
  stayLoggedIn: boolean
  authUserFail: boolean | null
}

const initialState: UserState = {
  profile: {
    id: '0',
    email: '',
    firstName: '',
    lastName: '',
    worksiteId: null,
    role: null,
    avatarUrl: '',
    hasRegistered: false,
    groups: [],
  },
  error: null,
  stayLoggedIn: null,
  authUserFail: null,
}

const login = createAction<LoginPayload>('user/login')
const socialLogin = createAction<SocialLoginPayload>('user/socialLogin')
const socialSignup = createAction<SocialSignupPayload>('user/socialSignup')
const ssoLogin = createAction<SsoLoginPayload>('user/ssoLogin')
const getUser = createAction('user/getUser')
const updateUser = createAction<UserSuccessPayload>('user/updateUser')
const resetPasswordSuccess = createAction<AxiosResponse>(
  'user/resetPasswordSuccess'
)

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    loginSuccess(state, action: any) {
      const { accessToken, accessExpiration } = action.payload
      const decodedToken = jwtDecode(accessToken)
      const tokenExp = accessExpiration
        ? Math.floor(new Date(accessExpiration).getTime() / 1000)
        : decodedToken['exp']

      setStoredTokenExp(tokenExp, action.meta.stayLoggedIn)

      // store the type of platform the user logged into in local storage
      setFleetPlatformType(action.meta.platformType)

      LocalStorage.setItem('isSocialLogin', false)
      return {
        ...state,
        error: {
          status: false,
          info: null,
        },
        stayLoggedIn: action.meta.stayLoggedIn,
      }
    },
    socialLoginSuccess(state, action: any) {
      const { accessToken, accessExpiration } = action.payload
      const decodedToken = jwtDecode(accessToken)
      const tokenExp = accessExpiration
        ? Math.floor(new Date(accessExpiration).getTime() / 1000)
        : decodedToken['exp']
      setStoredTokenExp(tokenExp, true)
      LocalStorage.setItem('isSocialLogin', true)
      // store the type of platform the user logged into in local storage
      setFleetPlatformType(action.meta.platformType)
      return {
        ...state,
        error: {
          status: false,
          info: null,
        },
        stayLoggedIn: true,
      }
    },
    socialSignupSuccess(state, action: any) {
      const { accessToken, accessExpiration } = action.payload
      const decodedToken = jwtDecode(accessToken)
      const tokenExp = accessExpiration
        ? Math.floor(new Date(accessExpiration).getTime() / 1000)
        : decodedToken['exp']
      setStoredTokenExp(tokenExp, true)
      LocalStorage.setItem('isSocialLogin', true)
      // store the type of platform the user logged into in local storage
      setFleetPlatformType(action.meta.platformType)
      return {
        ...state,
        error: {
          status: false,
          info: null,
        },
        stayLoggedIn: true,
      }
    },
    logout() {
      LocalStorage.removeItem('stayLoggedIn')
      clearStoredTokenExp()
      LocalStorage.removeItem('isSocialLogin')
      LocalStorage.removeItem('socialLoginType')
      return {
        ...initialState,
      }
    },
    getUserSuccess(state, action: PayloadAction<UserSuccessPayload>) {
      const user = action.payload
      SegmentService.track('user-login-success', { email: user.email })

      state.profile = {
        ...user,
        worksiteId: user.worksiteId || null,
        role: user.groups.length === 0 ? null : user.groups[0],
        avatarUrl: user.avatar,
        hasRegistered: false,
      }
      state.authUserFail = false
    },
    updateUserSuccess(state, action: PayloadAction<UserSuccessPayload>) {
      const user = action.payload
      SegmentService.track('user-login-success', { email: user.email })

      state.profile = {
        ...user,
        worksiteId: user.worksiteId || null,
        role: user.groups.length === 0 ? null : user.groups[0],
        avatarUrl: user.avatar,
        hasRegistered: false,
      }
      state.authUserFail = false
    },
    loginFailed(state, action: any) {
      state.error = {
        status: true,
        info: {
          code: action.error.status,
          message: action.error.message,
          statusText: action.error.statusText,
        },
      }
    },
    socialLoginFailed(state, action: any) {
      state.error = {
        status: true,
        info: {
          code: action.error.status,
          message: action.error.message,
          statusText: action.error.statusText,
        },
      }
    },
    ssoLoginSuccess(state, action: any) {
      const { accessToken, accessExpiration } = action.payload
      const decodedToken = jwtDecode(accessToken)
      const tokenExp = accessExpiration
        ? Math.floor(new Date(accessExpiration).getTime() / 1000)
        : decodedToken['exp']
      setStoredTokenExp(tokenExp, true)
      LocalStorage.setItem('isSocialLogin', true)
      // store the type of platform the user logged into in local storage
      setFleetPlatformType(action.meta.platformType)
      return {
        ...state,
        error: {
          status: false,
          info: null,
        },
        stayLoggedIn: true,
      }
    },
    ssoLoginFailed(state, action: any) {
      state.error = {
        status: true,
        info: {
          code: action.error.status,
          message: action.error.message,
          statusText: action.error.statusText,
        },
      }
    },
    getUserFailed(state) {
      state.authUserFail = true
    },
    idpInitiatedSsoSuccess(state, action: any) {
      // For IdP-initiated SSO, the JWT token is stored in cookies by the backend
      // Try to get token expiration from payload first, then from cookies
      let tokenExp = action.payload?.tokenExp

      if (!tokenExp) {
        // If not in payload, try to read from cookies (may be HttpOnly)
        tokenExp = getTokenExpirationFromCookies()
      }

      if (tokenExp) {
        setStoredTokenExp(tokenExp, true)
        LocalStorage.setItem('isSocialLogin', true)
      } else {
        // HttpOnly cookies hide exp; treat as expired so DefaultLayout refreshes soon.
        setStoredTokenExp(Math.floor(Date.now() / 1000), true)
        LocalStorage.setItem('isSocialLogin', true)
      }

      // Platform type can only be employee
      setFleetPlatformType('employee')

      return {
        ...state,
        error: {
          status: false,
          info: null,
        },
        stayLoggedIn: true,
      }
    },
  },
})

export const userReducer = userSlice.reducer
export const userActions = {
  ...userSlice.actions,
  login,
  socialLogin,
  socialSignup,
  ssoLogin,
  getUser,
  updateUser,
  resetPasswordSuccess,
}
